import { addDays, addMonths } from 'date-fns';

import { VISIT_STATUS, latestNextVisit, visitStatus } from './visit-status';
import { dateOfDay, dayOf } from './todo-text';

// ----------------------------------------------------------------------

// The words and choices for planning the next visit (#613). The rules that
// decide the visit status are in visit-status.js, shared with the server. A day
// is 'YYYY-MM-DD', so nothing shifts with time zones.

/** Today in Alberta, as 'YYYY-MM-DD'. */
export const albertaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Edmonton' }).format(new Date());

export const NEXT_VISIT_CHOICES = [
  { value: 'twoWeeks', label: 'In 2 weeks' },
  { value: 'oneMonth', label: 'In 1 month' },
  { value: 'date', label: 'Pick a date' },
  { value: 'none', label: 'No follow-up needed' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' → 'Oct 1, 2026' (read as a calendar day, never shifted). */
export const dayText = (day) => {
  const [y, m, d] = String(day).split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

/** The day a quick choice means, from today; null for the choices that aren't a fixed day. */
export const choiceDay = (choice, today) => {
  if (choice === 'twoWeeks') return dayOf(addDays(dateOfDay(today), 14));
  if (choice === 'oneMonth') return dayOf(addMonths(dateOfDay(today), 1));
  return null;
};

/**
 * What the dialog's choice sends the server: { by } or { noFollowUp: true };
 * null while the choice isn't complete (no date picked yet).
 */
export const nextVisitPayload = ({ choice, date }, today) => {
  if (choice === 'none') return { noFollowUp: true };
  const by = choice === 'date' ? date : choiceDay(choice, today);
  return by ? { by } : null;
};

/**
 * Why the choice can't be used, or null: a past day, or (Grade 12) one after
 * graduation. The server decides too; this saves the round trip.
 */
export const nextVisitProblem = ({ choice, date }, { grade, today }) => {
  const payload = nextVisitPayload({ choice, date }, today);
  if (!payload?.by) return null;
  if (payload.by < today) return 'Choose today or a later day.';
  const latest = latestNextVisit(grade, today);
  if (latest && payload.by > latest) return `A Grade 12 student's next visit can't be after graduation (${dayText(latest)}).`;
  return null;
};

/** How the status reads, as { text, color } for a Label/Chip. */
export const visitStatusLabel = (nextVisit, today) => {
  const status = visitStatus(nextVisit, today);
  switch (status) {
    case VISIT_STATUS.OVERDUE:
      return { status, text: `Overdue · ${dayText(nextVisit.by)}`, color: 'error' };
    case VISIT_STATUS.DUE_SOON:
      return { status, text: `Due soon · ${dayText(nextVisit.by)}`, color: 'warning' };
    case VISIT_STATUS.PLANNED:
      return { status, text: dayText(nextVisit.by), color: 'default' };
    case VISIT_STATUS.NO_FOLLOW_UP:
      return { status, text: 'No follow-up needed', color: 'default' };
    default:
      return { status, text: 'No plan', color: 'warning' };
  }
};
